import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useGTM from '../hooks/useGTM';
import { pushToDataLayer } from '../config/gtm';

// ---------------------------------------------
// Modal reutilizable
// ---------------------------------------------
type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
};

const Modal: React.FC<ModalProps> = ({ open, onClose, title, children }) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    if (open) {
      window.addEventListener('keydown', esc);
    }
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id="experience-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Modal'}
      className="fixed inset-0 z-[1000] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div className="relative z-[1001] w-full h-full sm:w-[85%] sm:h-auto sm:max-w-3xl sm:max-h-[90vh] md:w-[70%] bg-white sm:rounded-2xl shadow-2xl p-4 sm:p-6 flex flex-col">
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h3>
          <button
            aria-label="Cerrar"
            onClick={onClose}
            className="ml-4 rounded-full p-2 hover:bg-gray-100 text-2xl leading-none"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------
// Países (value = ISO2) + helpers GA4
// ---------------------------------------------
type CountryOpt = { value: string; label: string };

const COUNTRIES: CountryOpt[] = [
  { value: 'AR', label: 'Argentina' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CU', label: 'Cuba' },
  { value: 'DO', label: 'República Dominicana' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'HN', label: 'Honduras' },
  { value: 'MX', label: 'México' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'PA', label: 'Panamá' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'PE', label: 'Perú' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'ES', label: 'España' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'US', label: 'Estados Unidos' },
  { value: 'BR', label: 'Brasil' },
  { value: 'CA', label: 'Canadá' },
  { value: 'GB', label: 'Reino Unido' },
  { value: 'DE', label: 'Alemania' },
  { value: 'FR', label: 'Francia' },
  { value: 'IT', label: 'Italia' },
  { value: 'OTHER', label: 'Otro' },
];

const getCountryLabel = (iso2: string) =>
  COUNTRIES.find((c) => c.value === iso2)?.label || 'Otro';

const stripDiacritics = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const gaNormalizeCountry = (input: string) => {
  if (!input) return { iso2: 'OTHER', name: 'Otro' };

  const upper = input.toUpperCase().trim();

  if (COUNTRIES.some((c) => c.value === upper)) {
    return { iso2: upper, name: getCountryLabel(upper) };
  }

  const s = stripDiacritics(input).toLowerCase().trim();

  const map: Record<string, string> = {
    'ecuador': 'EC',
    'espana': 'ES',
    'españa': 'ES',
    'mexico': 'MX',
    'méxico': 'MX',
    'peru': 'PE',
    'perú': 'PE',
    'estados unidos': 'US',
    'eeuu': 'US',
    'ee.uu.': 'US',
    'usa': 'US',
    'united states': 'US',
    'reino unido': 'GB',
    'uk': 'GB',
    'inglaterra': 'GB',
    'brasil': 'BR',
    'canada': 'CA',
    'canadá': 'CA',
    'argentina': 'AR',
    'chile': 'CL',
    'colombia': 'CO',
    'uruguay': 'UY',
    'venezuela': 'VE',
    'bolivia': 'BO',
    'paraguay': 'PY',
    'panama': 'PA',
    'panamá': 'PA',
    'guatemala': 'GT',
    'honduras': 'HN',
    'nicaragua': 'NI',
    'el salvador': 'SV',
    'costa rica': 'CR',
    'republica dominicana': 'DO',
    'cuba': 'CU',
    'puerto rico': 'PR',
    'francia': 'FR',
    'alemania': 'DE',
    'italia': 'IT',
  };

  const iso2 = map[s] || 'OTHER';
  return { iso2, name: getCountryLabel(iso2) };
};

// ---------------------------------------------
// Tipos y constantes
// ---------------------------------------------
interface FormData {
  email: string;
  name: string;
  country: string; // ISO2 seleccionado
}

const AMAZON_KINDLE_URL =
  'https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520';

const ReservarFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { trackCTA, trackForm, trackAction, trackConv } = useGTM();
  const [formData, setFormData] = useState<FormData>({ email: '', name: '', country: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExperience, setShowExperience] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [expVideoMilestones, setExpVideoMilestones] = useState<Set<number>>(new Set());
  const experienceVideoRef = useRef<HTMLVideoElement>(null);

  // Body scroll lock al abrir modal
  useEffect(() => {
    document.body.style.overflow = showExperience ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showExperience]);

  // Track page view
  useEffect(() => {
    trackAction('view_form_page', 'page', 'reservar_form');
    pushToDataLayer({ event: 'view_form_page', page_path: '/reservar/formulario' });
  }, [trackAction]);

  const handleExpVideoProgress = () => {
    if (experienceVideoRef.current && experienceVideoRef.current.duration > 0) {
      const p = Math.round(
        (experienceVideoRef.current.currentTime / experienceVideoRef.current.duration) * 100
      );
      const milestones: number[] = [25, 50, 75, 100];
      const next = milestones.find((m) => p >= m && !expVideoMilestones.has(m));

      if (next) {
        setExpVideoMilestones((prev) => {
          const newSet = new Set(prev);
          newSet.add(next);
          return newSet;
        });
        const eventName = `experience_video_${next === 100 ? 'complete' : next}`;
        trackAction(eventName, 'video', 'experience_video');
        pushToDataLayer({ event: eventName });
      }
    }
  };

  const handleVideoPlay = () => {
    trackAction('experience_video_start', 'video', 'experience_video');
    pushToDataLayer({ event: 'experience_video_start' });
  };

  // Validación del formulario
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name?.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.country?.trim()) newErrors.country = 'El país es requerido';
    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openExperience = (source: string) => {
    setShowExperience(true);
    pushToDataLayer({ event: 'experience_reveal_open', source });
    trackAction('modal_open', 'ui', 'experience_reveal');
  };

  const closeExperience = () => {
    setShowExperience(false);
    trackAction('modal_close', 'ui', 'experience_reveal');
    pushToDataLayer({ event: 'experience_reveal_close' });
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    trackForm('submit', 'reservation_form', 'reservation');
    pushToDataLayer({ event: 'lead_form_submit', form_id: 'reservation_form', form_type: 'reservation' });

    try {
      // TODO: Integrar con backend real
      await new Promise((r) => setTimeout(r, 800));

      // Normalización país para GA4
      const { iso2, name } = gaNormalizeCountry(formData.country);

      // Conversión
      trackConv('reservation', null, 'USD');
      pushToDataLayer({
        event: 'generate_lead',
        user_role: 'parent',
        country_iso2: iso2,
        country_name: name,
        page_path: '/reservar/formulario',
      });

      // Flag de lead
      localStorage.setItem('lead_done', '1');

      // Mostrar experiencia en modal
      openExperience('form_submit');
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setErrors({ email: 'Error al procesar la reserva. Intenta de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCTAClick = (ctaId: string, section: string) => {
    trackCTA(ctaId, section, 'button');
    pushToDataLayer({ event: 'cta_click', cta_id: ctaId, cta_section: section, page_path: '/reservar/formulario' });
  };

  return (
    <div id="app-root" className="relative flex min-h-[100svh] flex-col bg-yellow-50 w-full overflow-x-clip">
      {/* Header */}
      <header className="bg-yellow-50 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img
                src="/images/princonser kids logo.png"
                alt="Princonser Kids Logo"
                className="h-10 sm:h-12 md:h-14 w-auto"
                loading="lazy"
              />
              <p className="hidden sm:block text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 leading-tight">
                Primera Experiencia Inmersiva de Educación Integral
              </p>
            </div>
            <button
              onClick={() => navigate('/reservar')}
              className="text-gray-600 hover:text-gray-900 text-sm sm:text-base"
            >
              ← Volver
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section con Formulario */}
        <section className="relative min-h-[100svh] flex items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50 py-12 sm:py-20">
          <div className="max-w-2xl w-full px-4 sm:px-6 lg:px-8">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">
                Reserva ahora nuestra <span className="text-yellow-600">primera experiencia</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 text-center">
                (8 libros + app + retos) para convertir conocimiento en hábitos, proyectos y liderazgo.
              </p>
              
              <form
                id="hero-form"
                onSubmit={handleSubmit}
                className="space-y-4 sm:space-y-6"
                aria-label="Formulario de reserva"
              >
                {/* Nombre */}
                <div>
                  <input
                    type="text"
                    placeholder="Tu nombre*"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    required
                    aria-label="Tu nombre"
                    onFocus={() => pushToDataLayer({ event: 'lead_form_open', source_section: 'form_page' })}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* País (select ISO2) */}
                <div>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.country ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    required
                    aria-label="País"
                  >
                    <option value="" disabled>Selecciona tu país*</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent`}
                    required
                    aria-label="Email"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => handleCTAClick('reserve_now_form', 'form_page')}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-4 px-6 rounded-xl text-lg sm:text-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Procesando...' : 'Reservar ahora nuestra primera experiencia'}
                </button>

                <p className="text-xs sm:text-sm text-gray-500 text-center">
                  ✅ Datos mínimos y privacidad garantizada
                </p>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400 text-sm sm:text-base">
              &copy; 2024 Princonser Kids. Todos los derechos reservados.
            </p>
            <p className="text-gray-400 text-sm sm:text-base mt-2">
              Formando niños sabios con propósito.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal de Experiencia (reveal) */}
      <Modal
        open={showExperience}
        onClose={closeExperience}
        title="Esto es lo que estás a punto de vivir"
      >
        <div className="text-center">
          <div className="relative w-full bg-black/80 rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-300 mb-6">
            <video
              ref={experienceVideoRef}
              className="w-full h-auto"
              controls
              preload="metadata"
              poster="/images/landingpage/video-poster.jpg"
              onPlay={handleVideoPlay}
              onTimeUpdate={handleExpVideoProgress}
              aria-label="Video de la experiencia que estás a punto de adquirir"
            >
              <source src="/videos/landingpage/video bruto v.1.mp4" type="video/mp4" />
              Tu navegador no soporta el elemento de video.
            </video>
          </div>
          
          <a
            href={AMAZON_KINDLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              handleCTAClick('amazon_click', 'experience_reveal');
              pushToDataLayer({ event: 'amazon_click', destination: 'amazon_kindle' });
            }}
            className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            La solución a la distancia de un clic
          </a>
          <p className="text-xs text-gray-500 mt-4">* Se abrirá Amazon Kindle en una nueva pestaña.</p>
        </div>
      </Modal>
    </div>
  );
};

export default ReservarFormPage;

